/**
 * Was die Details einer Buchung in der Belegung zeigen (Board „Belegung“, Popover und Blatt):
 * dieselbe Wahrheit am Desktop und am Handy. Ohne Gastdaten-Recht streicht der Server Name,
 * Telefon und E-Mail – dann heißt die Buchung „Buchung {Nummer}“ und es gibt keine Kontaktzeilen,
 * statt „nicht hinterlegt“ für etwas, das nur verborgen ist.
 */
import { formatMoney } from '@/shared/lib/format';
import { PRICE_UNDER_REVIEW } from '@/shared/lib/bookingPrice';
import { classifyChannel, type BookingSpan } from './calendarHelpers';
import { parseDate } from './calendarDates';
import { occupancyShort } from './calendarRowModel';

export interface BookingDetailsModel {
  title: string;
  sub: string;
  facts: { label: string; value: string }[];
  /** Plan-Banner; `attention`, wenn der Plan Klärung braucht (wie im Buchungsarbeitsraum). */
  plan: { text: string; attention: boolean } | null;
  /**
   * null: keine Kontaktzeilen, weil der Server die Felder für diese Rolle streicht. `email`
   * fehlt, wenn nur das Kontakt-Recht besteht – dann ist sie verborgen, nicht „nicht hinterlegt“.
   */
  contacts: { phone: string | null; email?: string | null } | null;
}

/** Wie der Server streicht: Name und Telefon ohne Kontakt-Recht, die E-Mail ohne volle Gastdaten. */
export interface GuestAccess {
  contact: boolean;
  email: boolean;
}

interface Context {
  guestAccess: GuestAccess;
  propertyName: string;
  currency: string;
  /** Uhrzeiten des Objekts („15:00:00“); fehlen sie, steht nur der Tag. */
  checkInTime?: string;
  checkOutTime?: string;
  today: string;
}

const WEEKDAY = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function day(iso: string, today: string): string {
  const date = parseDate(iso);
  const base = `${WEEKDAY[date.getUTCDay()]} ${date.getUTCDate()}.${date.getUTCMonth() + 1}.`;
  return iso.slice(0, 4) === today.slice(0, 4) ? base : `${base}${date.getUTCFullYear()}`;
}

const PLAN_VERB: Partial<Record<string, string>> = {
  Scheduled: 'geplant',
  Claimed: 'wird ausgeführt',
  NeedsAttention: 'braucht Klärung',
};

const clock = (time?: string) => (time ? ` · ${time.slice(0, 5)}` : '');

export function detailsTitle(span: BookingSpan, canSeeGuests: boolean): string {
  if (!canSeeGuests) return `Buchung ${span.bookingReference}`;
  return span.guestName || 'Ohne Gastnamen';
}

export function bookingDetailsModel(span: BookingSpan, ctx: Context): BookingDetailsModel {
  const departure = span.actualDepartureDate ?? span.checkOutDate;
  const price =
    span.totalPrice === null || span.priceEvidenceState !== 'Known'
      ? PRICE_UNDER_REVIEW
      : formatMoney(span.totalPrice, ctx.currency);
  const plan = span.earlyDeparturePlan;
  return {
    title: detailsTitle(span, ctx.guestAccess.contact),
    sub: ctx.guestAccess.contact
      ? `${ctx.propertyName} · #${span.bookingReference}`
      : `${ctx.propertyName} · ${classifyChannel(span.channel).label}`,
    facts: [
      { label: 'Anreise', value: `${day(span.checkInDate, ctx.today)}${clock(ctx.checkInTime)}` },
      {
        label: span.actualDepartureDate ? 'Abgereist' : 'Abreise',
        // Wann der Gast tatsächlich ging, weiß niemand – dann steht nur der Tag.
        value: `${day(departure, ctx.today)}${span.actualDepartureDate ? '' : clock(ctx.checkOutTime)}`,
      },
      { label: 'Nächte · Gäste', value: `${span.stayNights} · ${occupancyShort(span)}` },
      { label: 'Preis', value: price },
    ],
    plan: plan
      ? {
          text: `Vorzeitige Abreise ${PLAN_VERB[plan.status] ?? 'geplant'}: ${day(plan.plannedDepartureDate, ctx.today)} · Buchung endet ${day(span.checkOutDate, ctx.today)}`,
          attention: plan.status === 'NeedsAttention',
        }
      : null,
    contacts: !ctx.guestAccess.contact
      ? null
      : ctx.guestAccess.email
        ? { phone: span.phone, email: span.email }
        : { phone: span.phone },
  };
}
